import React from "react";
import styled from "styled-components";
import { FieldLabel, FieldWrap, Error } from "components/styled/Field";
import Clipboard, { Wrap } from "components/common/Clipboard";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { regentGray } from "utils/constants/colors";

const HelperText = styled.div`
  font-size: 12px;
  color: ${regentGray};
  margin-top: 8px;
`;

const CopyWrap = styled.div`
  display: flex;
  > ${Wrap} {
    margin-left: 8px;
  }
`;

const LabelWrapper = styled.span`
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-right: 4px;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  margin-left: 8px;
  font-size: 14px;
  vertical-align: middle;
`;

export const Label = ({ label, required, description }) => {
  const { t } = useTranslation();
  const fieldLabel =
    typeof label !== "string" ? (
      label
    ) : (
      <LabelWrapper title={label}>{label}</LabelWrapper>
    );
  return (
    <FieldLabel>
      {fieldLabel}
      {!required && t("(Optional)")}
      {description && (
        <Tooltip title={description} placement="right">
          <StyledInfoCircleOutlined />
        </Tooltip>
      )}
    </FieldLabel>
  );
};

export function Field(Component) {
  function FieldComponent(
    {
      required = true,
      showError = true,
      withCopy = false,
      label,
      validation,
      description,
      hidden,
      helperText,
      ...rest
    },
    ref
  ) {
    let error = null;

    if (validation) {
      error = showError && validation[0];
    }
    let component = (
      <Component
        ref={ref}
        {...rest}
        validateStatus={error && error.status}
        error={error}
      />
    );

    if (withCopy) {
      component = (
        <CopyWrap>
          {component}
          <Clipboard text={rest.value} />
        </CopyWrap>
      );
    }

    return (
      <FieldWrap
        className={error && `ant-form-item-has-${error.status}`}
        hidden={hidden}
      >
        {label && (
          <Label label={label} required={required} description={description} />
        )}
        {component}
        {error ? <Error>{error.result}</Error> : null}
        {helperText ? <HelperText>{helperText}</HelperText> : null}
      </FieldWrap>
    );
  }

  FieldComponent.displayName = `Field(${Component.displayName})`;

  return React.forwardRef(FieldComponent);
}
